import { Link } from 'react-router-dom'
import { ArrowRight, Users } from 'lucide-react'
import type { UserRsvpEntry } from '@fellowship/shared'
import { EventBadge } from '../ui/EventBadge'
import { getEventType } from '../ui/eventTypes'
import { formatDate, formatTime, GRADIENT, SHADOW_SOFT } from './format'

function formatMiles(meters: number): string {
  const miles = meters / 1609.34
  return miles < 10 ? `${miles.toFixed(1)} mi away` : `${Math.round(miles)} mi away`
}

export function UpNextCard({ entry }: { entry: UserRsvpEntry | null }) {
  if (!entry) {
    return (
      <div
        style={{
          padding: 28,
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 20,
          textAlign: 'center',
          boxShadow: SHADOW_SOFT,
        }}
      >
        <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--muted)' }}>No upcoming RSVPs.</p>
        <Link
          to="/search"
          style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}
        >
          Find events nearby →
        </Link>
      </div>
    )
  }

  const { occurrence, rsvp, church } = entry
  const title = occurrence.overrideTitle ?? occurrence.event.title
  const t = getEventType(occurrence.event.eventType)
  const going = rsvp.attendanceStatus === 'going'

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        borderLeft: `4px solid ${t.color}`,
        borderRadius: 20,
        boxShadow: SHADOW_SOFT,
        padding: '20px 22px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <EventBadge type={occurrence.event.eventType} size="md" />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: '3px 10px',
            borderRadius: 999,
            background: going ? 'var(--primary-soft)' : 'var(--surface-2)',
            color: going ? 'var(--primary)' : 'var(--muted)',
          }}
        >
          {going ? 'Going' : 'Maybe'}
        </span>
      </div>

      <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--ink)', lineHeight: 1.2 }}>
        {title}
      </div>
      {church && <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 5 }}>{church.name}</div>}

      <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span>{formatDate(occurrence.startsAt)}</span>
        <span style={{ color: 'var(--line)' }}>·</span>
        <span>{formatTime(occurrence.startsAt)}</span>
        {occurrence.distanceMeters !== undefined && (
          <>
            <span style={{ color: 'var(--line)' }}>·</span>
            <span>{formatMiles(occurrence.distanceMeters)}</span>
          </>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 18,
          paddingTop: 16,
          borderTop: '1px solid var(--line-soft)',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Users size={13} /> {occurrence.rsvpCount ?? 0} going
        </span>
        <Link to={`/events/${occurrence.event.id}`} style={{ textDecoration: 'none' }}>
          <span
            style={{
              height: 38,
              padding: '0 18px',
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13.5,
              fontWeight: 600,
              color: '#fff',
              background: GRADIENT,
              boxShadow: '0 3px 8px rgba(59,76,202,.25)',
            }}
          >
            View event <ArrowRight size={13} />
          </span>
        </Link>
      </div>
    </div>
  )
}
